<template>
  <div
    style="width: 560px; display: flex; align-items: center"
  >
    <div style="flex: 0; text-wrap: nowrap; white-space: nowrap">{{ title }}：</div>
    <el-select
      v-model="formValue.time_type"
      :placeholder="$t('common.pleaseSelect')"
      @change="emitParams"
      style="margin-right: 10px"
      :style="{ width: $t('common.mode') === 'zh-cn' ? '120px' : '160px' }"
    >
      <el-option
        v-for="item in selectOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </el-select>
    <!-- 日 -->
    <div v-if="formValue.time_type === '1'" style="flex: 1;">
      <el-date-picker
        v-model="formValue.day_date"
        type="daterange"
        :disabled-date="disabledDate"
        :clearable="false"
        :editable="false"
        format="YYYY-MM-DD"
        value-format="YYYY-MM-DD"
        style="width: 100%"
        @change="dayDateChange"
      />
    </div>
    <!-- 月 -->
    <div v-if="formValue.time_type === '2'" style="flex: 1;">
      <el-date-picker
        v-model="formValue.month_date"
        type="month"
        :disabled-date="disabledDate"
        :clearable="false"
        :editable="false"
        format="YYYY-MM"
        value-format="YYYY-MM"
        style="width: 100%"
        @change="emitParams"
      />
    </div>
    <!-- 年 -->
    <div v-if="formValue.time_type === '3'" style="flex: 1;">
      <el-date-picker
        v-model="formValue.year_date"
        type="year"
        :disabled-date="disabledDate"
        :format="$t('commonComponents.yearFormat')"
        :clearable="false"
        :editable="false"
        value-format="YYYY"
        style="width: 100%"
        @change="emitParams"
      />
    </div>
    <!-- 季度 -->
    <div v-if="formValue.time_type === '4'" style="flex: 1;">
      <Quarter
        ref="quarterValue"
        :format="$t('commonComponents.quarterFormat')"
        style="width: 100%"
        value-format="YYYYQQ"
        :clearable="false"
        @update:modelValue="quarterDateHandle"
        v-model="formValue.quarter_date"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { ElMessage } from "element-plus";
import dayjs from "dayjs";
import { statisticalModeOptions } from "@/constant/mapConstant";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import Quarter from "./quarter.vue";
import { $t } from "@/i18n";

// 加载插件
dayjs.extend(quarterOfYear);

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: () => $t("common.statisticsTime"),
  },
  fieldConfig: {
    type: Object,
    default: {
      start_date: "start_date",
      end_date: "end_date",
    },
  },
  selectOptions: {
    type: Array,
    default: statisticalModeOptions,
  },
  // 初始化是否自动触发change事件
  autoEmit: {
    type: Boolean,
    default: true,
  },
  // 是否禁用当天
  disabledToday: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["change"]);

onMounted(() => {
  if (props.autoEmit) {
    emitParams();
  }
});

const getDefaultDayDate = () => {
  const date = props.disabledToday ? dayjs().subtract(1, "day") : dayjs();
  return [date.format("YYYY-MM-DD"), date.format("YYYY-MM-DD")];
};

const formValue = ref({
  time_type: props.selectOptions[0].value,
  // 禁用当天，默认前一天；否则，默认当天
  day_date: getDefaultDayDate(),
  // 默认当前月
  month_date: dayjs().format("YYYY-MM"),
  // 默认当前年
  year_date: dayjs().format("YYYY"),
  // // 默认当前季度
  quarter_date:
    dayjs().format("YYYY") + String(dayjs().quarter()).padStart(2, "0"),
});

function getFormParams() {
  let data = {
    time_type: formValue.value.time_type,
  };
  // 天
  if (formValue.value.time_type === "1" && formValue.value.day_date) {
    data.start_date = dayjs(formValue.value.day_date[0]).format("YYYY-MM-DD");
    data.end_date = dayjs(formValue.value.day_date[1]).format("YYYY-MM-DD");
  }
  // 月
  if (formValue.value.time_type === "2" && formValue.value.month_date) {
    // 当月的第一天
    data.start_date = dayjs(formValue.value.month_date)
      .startOf("month")
      .format("YYYY-MM-DD");
    data.end_date = dayjs(formValue.value.month_date)
      .endOf("month")
      .format("YYYY-MM-DD");
  }
  // 年
  if (formValue.value.time_type === "3" && formValue.value.year_date) {
    data.start_date = dayjs(formValue.value.year_date)
      .startOf("year")
      .format("YYYY-MM-DD");
    data.end_date = dayjs(formValue.value.year_date)
      .endOf("year")
      .format("YYYY-MM-DD");
  }
  // 季度
  if (formValue.value.time_type === "4" && formValue.value.quarter_date) {
    // 根据quarter_date获取季度第一天
    const quarterDate = formValue.value.quarter_date;
    const year = quarterDate.substring(0, 4);
    const quarter = quarterDate.substring(4);
    const quarterMonthMap = {
      "01": "01", // 第一季度 1-3月
      "02": "04", // 第二季度 4-6月
      "03": "07", // 第三季度 7-9月
      "04": "10", // 第四季度 10-12月
    };
    const month = quarterMonthMap[quarter] || "";
    data.start_date = `${year}-${month}-01`;
    data.end_date = dayjs(`${year}-${month}-01`)
      .add(2, "month")
      .endOf("month")
      .format("YYYY-MM-DD");
  }

  // 计算结束后，判断是否禁用当天
  if (props.disabledToday) {
    const today = dayjs().format("YYYY-MM-DD");
    const start = data.start_date;
    const end = data.end_date;
    // 判断时间是否包含当天，如果包含，则结束时间为当天的前一天
    if (start <= today && today <= end) {
      data.end_date = dayjs(today).subtract(1, "day").format("YYYY-MM-DD");
    }
  }

  return {
    [props.fieldConfig.start_date]: data.start_date,
    [props.fieldConfig.end_date]: data.end_date,
    time_type: data.time_type,
  };
}

function quarterDateHandle(e) {
  formValue.value.quarter_date = e;
  emitParams();
}

function emitParams() {
  emit("change", getFormParams());
}

function disabledQuarter(date) {
  return (
    date > dayjs("202501", "YYYYMM") || date < dayjs("20230401", "YYYYMMDD")
  );
}

const dayDateChange = (value) => {
  const start = dayjs(value[0]);
  const end = dayjs(value[1]);
  const diff = end.diff(start, "days");
  if (diff > 31) {
    ElMessage.error($t("commonComponents.maximumDays", [31]));
    return;
  }
  emitParams();
};
const disabledDate = (current) => {
  if (props.disabledToday) {
    return current && current >= dayjs().startOf("day");
  }
  if (props.mct_temporary_type) {
    const sixMonthsAgo = dayjs().subtract(6, "months").startOf("day");
    return (
      current && (current >= dayjs().startOf("day") || current < sixMonthsAgo)
    );
  } else {
    return current && current > dayjs().startOf("day");
  }
};

function resetForm(flag) {
  formValue.value = {
    time_type: props.selectOptions[0].value,
    day_date: getDefaultDayDate(),
    month_date: dayjs().format("YYYY-MM"),
    year_date: dayjs().format("YYYY"),
    quarter_date:
      dayjs().format("YYYY") + String(dayjs().quarter()).padStart(2, "0"),
  };
  if (!flag) {
    emitParams();
  }
}

function setFormParams(value) {
  const { time_type, start_date, end_date } = value;
  formValue.value.time_type = time_type;
  if (time_type === "1") {
    formValue.value.day_date = [start_date, end_date];
  } else if (time_type === "2") {
    formValue.value.month_date = start_date.substring(0, 7);
  } else if (time_type === "3") {
    formValue.value.year_date = start_date.substring(0, 4);
  } else if (time_type === "4") {
    formValue.value.quarter_date =
      start_date.substring(0, 4) +
      String(dayjs(start_date).quarter()).padStart(2, "0");
  }
}

defineExpose({
  resetForm,
  setFormParams,
  getFormParams,
});
</script>

<style scoped lang="scss"></style>
